import { CloudUploadOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import type { ChangeEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { v4 as uuidv4 } from "uuid";

import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { createPrivilege } from "../../services/crm.service";
import { uploadImage } from "../../services/upload.service";
import { PrivilegeCreateRequest } from "../../types/crm";

type FormState = {
  name: string;
  state_privileges: string;
  points_required: string;
  total_permissions: string;
  per_user_limit: string;
  start_date: Dayjs | null;
  end_date: Dayjs | null;
  details: string;
};

const initialForm: FormState = {
  name: "",
  state_privileges: "",
  points_required: "",
  total_permissions: "",
  per_user_limit: "",
  start_date: null,
  end_date: null,
  details: "",
};

const requiredKeys: Array<keyof FormState> = [
  "name",
  "state_privileges",
  "points_required",
  "total_permissions",
  "per_user_limit",
  "start_date",
  "end_date",
  "details",
];

const stateOptions = [
  { label: "Active", value: "Active" },
  { label: "Draft", value: "Draft" },
  // { label: "Fully Redeemed", value: "Fully Redeemed" }, // controlled by backend
  { label: "Expired", value: "Expired" },
];

const getStateChipStyles = (value: string) => {
  if (value === "Active") {
    return { bgcolor: "#E3F5E3", color: "#2D6830" };
  }
  if (value === "Draft") {
    return { bgcolor: "#EDEEF1", color: "#25272C" };
  }
  if (value === "Fully Redeemed") {
    return { bgcolor: "#ECE9FE", color: "#662BD6" };
  }
  return { bgcolor: "#FFE3E1", color: "#BE2217" };
};

const getErrorMessage = (fieldKey: string): string => {
  const messages: Record<string, string> = {
    name: "Please enter a privilege name",
    state_privileges: "Please select a category",
    points_required: "Please enter the points required",
    total_permissions: "Please enter the total limit (min 1)",
    per_user_limit: "Please enter the per user limit (min 1)",
    start_date: "Please select a start date",
    end_date: "Please select an end date",
    end_date_before_start: "End date must be after the start date",
    per_user_limit_exceeds: "Per user limit cannot exceed total limit",
    details: "Please provide details about this privilege",
    image: "Please upload a privilege image",
  };
  return messages[fieldKey] || "This field is required";
};

export default function CrmPrivilegesCreate() {
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { showSnackbar } = useSnackbar();
  const [form, setForm] = useState<FormState>(initialForm);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setBreadcrumbs([
      { label: "Privilege List", to: "/crm/privileges" },
      { label: "Create Privilege", to: "/crm/privileges/create" },
    ]);
  }, [setBreadcrumbs]);

  const dateRangeError = useMemo(() => {
    if (!form.start_date || !form.end_date) return null;
    if (form.end_date.isBefore(form.start_date, "day") || form.end_date.isSame(form.start_date, "day")) {
      return "end_date_before_start";
    }
    return null;
  }, [form.start_date, form.end_date]);

  const perUserLimitError = useMemo(() => {
    const total = Number(form.total_permissions);
    const perUser = Number(form.per_user_limit);
    if (form.total_permissions && form.per_user_limit && perUser > total) {
      return "per_user_limit_exceeds";
    }
    return null;
  }, [form.total_permissions, form.per_user_limit]);

  const missing = useMemo(() => {
    if (!submitted) return new Set<string>();

    return new Set(
      requiredKeys.filter((key) => {
        if (key === "start_date" || key === "end_date") {
          return !form[key];
        }
        if (key === "total_permissions" || key === "per_user_limit") {
          const val = Number(form[key]);
          return !String(form[key] || "").trim() || val < 1;
        }
        return !String(form[key] || "").trim();
      }).map(String),
    );
  }, [form, submitted]);

  const onFieldChange =
    (key: keyof FormState) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [key]: event.target.value }));
    };

  const onDateChange =
    (key: "start_date" | "end_date") =>
    (value: Dayjs | null) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(file ? URL.createObjectURL(file) : null);
  };

  const handleDiscard = () => {
    navigate("/crm/privileges");
  };

  const handleCreate = async () => {
    setSubmitted(true);
    const hasMissing = requiredKeys.some((key) => {
      if (key === "start_date" || key === "end_date") return !form[key];
      if (key === "total_permissions" || key === "per_user_limit") {
        const val = Number(form[key]);
        return !String(form[key] || "").trim() || val < 1;
      }
      return !String(form[key] || "").trim();
    });
    const hasNoFile = !selectedFile;

    if (hasMissing || hasNoFile || dateRangeError || perUserLimitError) return;

    setIsSubmitting(true);
    try {
      // Upload image first
      const fileExtension = selectedFile!.name.split(".").pop();
      const formData = new FormData();
      formData.append("file", selectedFile!);
      formData.append("file-name", `${uuidv4()}.${fileExtension}`);

      const uploadResponse = await uploadImage(formData);
      if (!uploadResponse?.fileName) {
        showSnackbar("Failed to upload image", "error");
        return;
      }

      const payload: PrivilegeCreateRequest = {
        name: form.name.trim(),
        category: form.state_privileges.trim(),
        details: form.details.trim(),
        points_required: Number(form.points_required) || 0,
        per_user_limit: Number(form.per_user_limit) || 0,
        total_limit: Number(form.total_permissions) || 0,
        banner_image: uploadResponse.fileName,
        start_date: form.start_date?.toISOString() || "",
        end_date: form.end_date?.toISOString() || "",
      };

      await createPrivilege(payload);
      showSnackbar("Privilege created successfully", "success");
      navigate("/crm/privileges");
    } catch (error) {
      console.error("Error creating privilege:", error);
      showSnackbar("Failed to create privilege", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={3}>
      <Paper sx={{ borderRadius: 1, overflow: "hidden" }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #D8DBDF" }}>
          <Typography sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}>
            Privilege Info
          </Typography>
        </Box>

        <Box sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
            <Stack spacing={2} sx={{ flex: 1 }}>
              <TextField
              variant="outlined"
                label="Privilege Name"
                placeholder="Enter Privilege Name"
                value={form.name}
                onChange={onFieldChange("name")}
                error={missing.has("name")}
                helperText={missing.has("name") ? getErrorMessage("name") : " "}
                required
                fullWidth
                 slotProps={{
                 inputLabel: { shrink: true },
                 }}
              />

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  select
                  label="Category"
                  value={form.state_privileges}
                  onChange={onFieldChange("state_privileges")}
                  required
                  slotProps={{
                    inputLabel: { shrink: true },
                    select: {
                      displayEmpty: true,
                      renderValue: (selected) => {
                        if (!selected)
                          return (
                            <span style={{ color: "#9e9e9e" }}>
                              Select Category
                            </span>
                          );
                        const value = String(selected);
                        return (
                          <Chip
                            size="small"
                            label={value}
                            sx={{
                              borderRadius: "4px",
                              px: 0.5,
                              height: 24,
                              fontSize: 12,
                              ...getStateChipStyles(value),
                            }}
                          />
                        );
                      },
                    },
                  }}
                  error={missing.has("state_privileges")}
                  helperText={
                    missing.has("state_privileges") ? getErrorMessage("state_privileges") : " "
                  }
                  fullWidth
                >
                  {stateOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Chip
                        size="small"
                        label={option.label}
                        sx={{
                          borderRadius: "4px",
                          px: 0.5,
                          height: 24,
                          fontSize: 12,
                          ...getStateChipStyles(option.value),
                        }}
                      />
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Points Required"
                  placeholder="Enter Points Required"
                  type="number"
                  value={form.points_required}
                  onChange={onFieldChange("points_required")}
                  error={missing.has("points_required")}
                  helperText={missing.has("points_required") ? getErrorMessage("points_required") : " "}
                  required
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Total Limit"
                  placeholder="Enter Total Limit"
                  type="number"
                  value={form.total_permissions}
                  onChange={onFieldChange("total_permissions")}
                  error={missing.has("total_permissions")}
                  helperText={
                    missing.has("total_permissions") ? getErrorMessage("total_permissions") : " "
                  }
                  required
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  label="Per User Limit"
                  placeholder="Enter Per User Limit"
                  type="number"
                  value={form.per_user_limit}
                  onChange={onFieldChange("per_user_limit")}
                  error={missing.has("per_user_limit") || !!perUserLimitError}
                  helperText={
                    missing.has("per_user_limit")
                      ? getErrorMessage("per_user_limit")
                      : perUserLimitError
                      ? getErrorMessage(perUserLimitError)
                      : " "
                  }
                  required
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <DatePicker
                  label="Start Date"
                  value={form.start_date}
                  onChange={onDateChange("start_date")}
                  maxDate={form.end_date ?? undefined}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: missing.has("start_date"),
                      helperText: missing.has("start_date") ? getErrorMessage("start_date") : " ",
                      InputLabelProps: { shrink: true },
                      placeholder: "Select Start Date",
                    },
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={form.end_date}
                  onChange={onDateChange("end_date")}
                  minDate={form.start_date ? form.start_date.add(1, "day") : undefined}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: missing.has("end_date") || !!dateRangeError,
                      helperText: missing.has("end_date")
                        ? getErrorMessage("end_date")
                        : dateRangeError
                        ? getErrorMessage(dateRangeError)
                        : " ",
                      InputLabelProps: { shrink: true },
                      placeholder: "Select End Date",
                    },
                  }}
                />
              </Stack>

              <Box>
                <Typography sx={{ fontSize: 14, mb: 0.75 }}>
                  <span style={{ color: "#d32f2f" }}>*</span> Upload Privilege Image
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderStyle: "dashed",
                    bgcolor: "#EDEEF1",
                    textAlign: "center",
                    borderColor: submitted && !selectedFile ? "#d32f2f" : "#bdbdbd",
                    borderWidth: submitted && !selectedFile ? "2px" : "1px",
                  }}
                >
                  <Typography sx={{ fontSize: 14, color: "#5F6D78" }}>
                    Drag and drop files here or click
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#9CADB6", mb: 2 }}>
                    Upload up to 5 files
                  </Typography>
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<CloudUploadOutlined />}
                    sx={{
                      borderRadius: "999px",
                      px: 3,
                      py: 0.75,
                      textTransform: "none",
                      bgcolor: "#111111",
                      "&:hover": { bgcolor: "#111111" },
                    }}
                  >
                    Upload
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={onFileChange}
                    />
                  </Button>
                  {selectedFile && previewUrl && (
                    <Box sx={{ mt: 1.5 }}>
                      <Box
                        component="img"
                        src={previewUrl}
                        alt={selectedFile.name}
                        sx={{
                          maxWidth: "100%",
                          maxHeight: 160,
                          borderRadius: 1,
                          objectFit: "contain",
                        }}
                      />
                      <Typography
                        sx={{ mt: 0.5, fontSize: 12, color: "#5F6D78" }}
                      >
                        {selectedFile.name}
                      </Typography>
                    </Box>
                  )}
                </Paper>
                {submitted && !selectedFile && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#d32f2f",
                      mt: 0.75,
                      ml: 1.75,
                    }}
                  >
                    {getErrorMessage("image")}
                  </Typography>
                )}
              </Box>
            </Stack>

            <Box sx={{ flex: 1 }}>
              <TextField
                label="Privilege Details"
                placeholder="Please specify all the details required to use the privilege."
                multiline
                minRows={19}
                value={form.details}
                required
                onChange={onFieldChange("details")}
                error={missing.has("details")}
                helperText={missing.has("details") ? getErrorMessage("details") : " "}
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            sx={{ pt: 3 }}
          >
            <Button
              variant="outlined"
              onClick={handleDiscard}
              sx={{
                borderRadius: "999px",
                px: 3,
                textTransform: "none",
                borderColor: "#111111",
                color: "#111111",
              }}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={isSubmitting}
              sx={{
                borderRadius: "999px",
                px: 3,
                textTransform: "none",
                bgcolor: "#111111",
                "&:hover": { bgcolor: "#111111" },
              }}
            >
              Create
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Stack>
    </LocalizationProvider>
  );
}
